import { useIsClient } from "@bmi-digital/components/hooks";
import Section from "@bmi-digital/components/section";
import TableOfContent from "@bmi-digital/components/table-of-content";
import { styled, useTheme } from "@mui/material/styles";
import { graphql, Script } from "gatsby";
import React, { useEffect } from "react";
import BackToResults from "../../../components/BackToResults";
import Breadcrumbs from "../../../components/Breadcrumbs";
import LeadBlockSection from "../../../components/LeadBlockSection";
import Page from "../../../components/Page";
import ProgressIndicator from "../../../components/ProgressIndicator";
import Sections from "../../../components/Sections";
import { useConfig } from "../../../contexts/ConfigProvider";
import { updateBreadcrumbTitleFromPayload } from "../../../utils/breadcrumbUtils";
import {
  generateHeroLevel,
  generateHeroProps
} from "../../../utils/heroLevelUtils";
import { renderHero } from "../../../utils/heroTypesUI";
import type { PageData as PageData } from "../../../components/Page";
import type {
  LeadBlockData,
  LeadBlockSectionData
} from "../../../components/LeadBlockSection";
import type { BreadcrumbData as BreadcrumbsData } from "../../../components/Breadcrumbs";
import type { TitleWithContentSectionData } from "../../../components/TitleWithContentSection";
import type { SpotlightHeroProps } from "@bmi-digital/components/spotlight-hero";
import type { HeroProps } from "@bmi-digital/components/hero";
import type { HomePageData, SiteData } from "../../../components/Site";
import type { SEOContentData } from "../../../components/SEOContent";
import type { PayloadCookiePolicyPage } from "../../../types/payload";
import type { MicroCopy as MicroCopyData } from "../../../components/MicroCopy";
import type { CalculatorConfig } from "../../../components/pitched-roof-calculator/types";
import type { HouseType } from "../../../components/visualiser/Types";
import type { GeneralErrorData } from "../../general-error";
import type {
  CountryNavigation,
  SampleOrder
} from "../../../components/Header";
import type { Visualiser } from "../../../components/Visualiser";

export type Data = Pick<
  PayloadCookiePolicyPage,
  "title" | "slug" | "path" | "heroType" | "subtitle"
> & {
  meta: SEOContentData;
  breadcrumbs: readonly BreadcrumbsData[];
  leadBlock: readonly [LeadBlockData] | null;
  sections: readonly (LeadBlockSectionData | TitleWithContentSectionData)[];
};

export type Props = {
  data: {
    payloadCookiePolicyPage: Data;
    payloadSite: SiteData;
    allPayloadMicroCopy: {
      nodes: MicroCopyData[];
    };
    payloadPitchedRoofCalculatorConfig: CalculatorConfig | null;
    allPayloadHouseModel: {
      nodes: HouseType[];
    };
    payloadHomePage: HomePageData;
    payloadGeneralError: GeneralErrorData;
    payloadCountryNavigation: CountryNavigation;
    payloadSampleOrder: SampleOrder | null;
    payloadVisualiser: Visualiser | null;
  };
  pageContext: {
    variantCodeToPathMap?: Record<string, string>;
  };
};

export const StyledSpinner = styled("div")(({ theme }) => ({
  backgroundColor: theme.colours.white,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "calc(100vh - 100px)" /* container margins and paddings */
}));

const Loading = () => (
  <StyledSpinner>
    <ProgressIndicator size={40} />
  </StyledSpinner>
);

export const StyledOnetrustContainer = styled("div")(({ theme }) => ({
  backgroundColor: theme.colours.white,
  padding: "0 !important",
  [`& > div`]: {
    display: "block"
  }
}));

const CookiePolicyPage = ({ data, pageContext }: Props) => {
  const { isClient } = useIsClient();
  const theme = useTheme();
  const { title, subtitle, leadBlock, sections, heroType, breadcrumbs, meta } =
    data.payloadCookiePolicyPage;
  const enhancedBreadcrumbs = updateBreadcrumbTitleFromPayload(breadcrumbs, "");

  const { isBrandProviderEnabled, oneTrustId } = useConfig();
  const heroLevel = generateHeroLevel(heroType, enhancedBreadcrumbs);
  const heroProps: HeroProps | SpotlightHeroProps = generateHeroProps(
    title,
    heroLevel,
    subtitle,
    null,
    null,
    null,
    theme
  );

  useEffect(() => {
    let attempts = 0;
    const interval = setInterval(() => {
      if (window.OneTrust) {
        window.OneTrust.initializeCookiePolicyHtml();
        clearInterval(interval);
      }

      attempts++;
      if (attempts > 60) {
        //The interval will be triggered 60 times every 500 milliseconds,
        //which is equal to 30 seconds.
        //Such a large timeout has been used to ensure that the cookies table
        // is also rendered for users with a very slow internet connection.
        clearInterval(interval);
      }
    }, 500);

    return () => clearInterval(interval);
  }, [oneTrustId]);

  const isDarkThemed = heroLevel !== 3;
  const breadcrumbsNode = (
    <BackToResults isDarkThemed={isDarkThemed}>
      <Breadcrumbs
        data={enhancedBreadcrumbs}
        isDarkThemed={isDarkThemed}
        data-testid="simple-page-breadcrumbs-top"
      />
    </BackToResults>
  );
  const pageData: PageData = {
    breadcrumbs: enhancedBreadcrumbs,
    meta,
    path: data.payloadCookiePolicyPage.path,
    signupBlock: null
  };

  const isHeroKeyLine = Boolean(isBrandProviderEnabled);

  return (
    <Page
      brand={null}
      title={title}
      pageData={pageData}
      siteData={data.payloadSite}
      microCopy={data.allPayloadMicroCopy.nodes}
      pitchedRoofCalculatorConfig={data.payloadPitchedRoofCalculatorConfig}
      visualiserHouseTypes={data.allPayloadHouseModel.nodes}
      homePage={data.payloadHomePage}
      generalError={data.payloadGeneralError}
      countryNavigation={data.payloadCountryNavigation}
      sampleOrder={data.payloadSampleOrder}
      visualiser={data.payloadVisualiser}
      variantCodeToPathMap={pageContext?.variantCodeToPathMap}
      ogImageUrl={""}
    >
      {renderHero(heroProps, breadcrumbsNode, heroType, {
        isHeroKeyLine: isHeroKeyLine
      })}
      <TableOfContent>
        {leadBlock && leadBlock[0] && <LeadBlockSection data={leadBlock[0]} />}
        {isClient && (
          <Section backgroundColor="white">
            <StyledOnetrustContainer
              id="ot-sdk-cookie-policy"
              data-testid="ot-sdk-cookie-policy"
            />
          </Section>
        )}
        {sections && <Sections data={sections} startIndex={+!!leadBlock} />}
        <Section
          backgroundColor="alabaster"
          isSlim
          data-testid="breadcrumbs-section-bottom"
        >
          <BackToResults>
            <Breadcrumbs
              data={enhancedBreadcrumbs}
              data-testid="simple-page-breadcrumbs-bottom"
            />
          </BackToResults>
        </Section>
      </TableOfContent>
      {!isClient && <Loading />}
    </Page>
  );
};

export const Head = () => {
  const { oneTrustId } = useConfig();

  return (
    <>
      {Boolean(oneTrustId) && (
        <Script
          src="https://cdn.cookielaw.org/scripttemplates/otSDKStub.js"
          type="text/javascript"
          data-domain-script={oneTrustId}
          strategy="post-hydrate"
        />
      )}
    </>
  );
};

export default CookiePolicyPage;

export const pageQuery = graphql`
  query CookiePolicyPageById($pageId: String!) {
    payloadCookiePolicyPage(id: { eq: $pageId }) {
      ...PayloadCookiePolicyPageFragment
      title
      slug
      heroType
      subtitle
      leadBlock {
        ...LeadBlockFragment
      }
      sections {
        ... on PayloadLeadBlockSection {
          ...LeadBlockSectionFragment
        }
        ... on PayloadTitleWithContent {
          ...TitleWithContentSectionFragment
        }
      }
    }
    payloadSite {
      ...SiteFragment
    }
    allPayloadMicroCopy {
      nodes {
        ...MicroCopyFragment
      }
    }
    payloadPitchedRoofCalculatorConfig {
      ...PitchedRoofCalculatorFragment
    }
    allPayloadHouseModel {
      nodes {
        ...VisualiserHouseFragment
      }
    }
    payloadHomePage {
      ...HomePageFragment
    }
    payloadGeneralError {
      ...GeneralErrorFragment
    }
    payloadCountryNavigation {
      ...CountryNavigationFragment
    }
    payloadSampleOrder {
      ...SampleOrderFragment
    }
    payloadVisualiser {
      ...VisualiserFragment
    }
  }
`;
